// components/StepManager.tsx
import { Autosuggest, Button, Icon } from '@cloudscape-design/components';
import { useState } from 'react';
import { getStepTypeLabel, suggestStepType, type Step } from '../models/Recipe';
import Flex from './Flex';
import StepModal from './StepModal';

type StepManagerProps = {
  steps: Step[];
  stepOptions: { value: string; label: string; type: string }[];
  onChange: (steps: Step[]) => void;
  onImportStep: (selected: any) => void;
};

export default function StepManager({ steps, stepOptions, onChange, onImportStep }: StepManagerProps) {
  const [visibleModal, setVisibleModal] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [stepQ, setStepQ] = useState('');

  const handleSave = (newStep: Step) => {
    const updated = [...steps];
    if (editIndex === null) {
      updated.push(newStep);
    } else {
      updated[editIndex] = newStep;
    }
    onChange(updated);
    setVisibleModal(false);
    setEditIndex(null);
  };

  const shiftUp = (i: number) => {
    if (i === 0) return;
    const updated = [...steps];
    const [removed] = updated.splice(i, 1);
    updated.splice(i - 1, 0, removed);
    onChange(updated);
  };

  const shiftDown = (i: number) => {
    if (i === steps.length - 1) return;
    const updated = [...steps];
    const [removed] = updated.splice(i, 1);
    updated.splice(i + 1, 0, removed);
    onChange(updated);
  };

  const removeStep = (i: number) => {
    onChange(steps.filter((_, idx) => idx !== i));
  };

  let day = 0;

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', gap: 5, marginTop: 10, marginBottom: 10 }}>
        <Button
          wrapText={false}
          onClick={() => {
            setEditIndex(null);
            setVisibleModal(true);
          }}
        >
          Add step
        </Button>
        <Autosuggest
          placeholder="Import step"
          value={stepQ}
          onChange={({ detail }) => setStepQ(detail.value)}
          options={stepQ ? stepOptions : []}
          onSelect={({ detail }) => onImportStep(detail.selectedOption)}
        />
      </div>

      {steps.map((step, i) => {
        day = i === 0 ? step.duration : day + step.duration;
        return (
          <Flex
            key={i}
            gap={0}
            align="start"
            justify="start"
            style={{ marginBottom: 20, borderBottom: '1px solid #ccc', paddingBottom: 20 }}
          >
            <Flex column align="start" gap={2}>
              <Flex gap={2}>
                <Button iconName="angle-up" disabled={i === 0} onClick={() => shiftUp(i)} />
                <Button disabled={i === steps.length - 1} iconName="angle-down" onClick={() => shiftDown(i)} />
              </Flex>

              <Flex gap={2}>
                <Button
                  iconName="edit"
                  onClick={() => {
                    setEditIndex(i);
                    setVisibleModal(true);
                  }}
                />
                <Button iconName="close" onClick={() => removeStep(i)} />
              </Flex>
            </Flex>
            <Flex column align="start">
              <span
                style={{
                  marginLeft: 10,
                  marginRight: 10,
                  fontWeight: 'bold',
                  fontStyle: step.type ? 'normal' : 'italic',
                  color: step.type ? 'inherit' : 'indigo',
                }}
              >
                {step.type ? getStepTypeLabel(step.type) : getStepTypeLabel(suggestStepType(step) ?? 'misc')}{' '}
                {!step.type && (
                  <span>
                    <Icon name="gen-ai" />
                  </span>
                )}
              </span>
              <span style={{ flex: 1, marginLeft: 10 }}>
                Day {day - step.duration} – {step.name} – {step.duration} days – {step.temperature}°C – {step.gravity}{' '}
                SG – {step.description}
              </span>
            </Flex>
          </Flex>
        );
      })}

      <StepModal
        visible={visibleModal}
        initial={editIndex !== null ? steps[editIndex] : null}
        onDismiss={() => {
          setVisibleModal(false);
          setEditIndex(null);
        }}
        onSave={handleSave}
      />
    </div>
  );
}
